import type { ReactNode } from "react";
import type { RoomView } from "@grundbesitz/shared";
import { formatMoney } from "../../lib/format";
import { currentPlayerOf, isSpectating, openTradesToMe, selfPlayerOf, unreadChat } from "../../lib/gameView";
import { Board } from "../board/Board";
import { TokenIcon } from "../board/TokenIcon";
import { Sheet } from "../ui/Sheet";
import { ActionBar } from "./ActionBar";
import { AssetsPanel } from "./AssetsPanel";
import { AuctionDialog } from "./AuctionDialog";
import { CardOverlay } from "./CardOverlay";
import { ChatLogPanel } from "./ChatLogPanel";
import { CompactLog } from "./CompactLog";
import { FieldSheet } from "./FieldSheet";
import { GameHeader } from "./GameHeader";
import { HotkeyHelp } from "./HotkeyHelp";
import { useEventEffects, useGameLog, useIsDesktop } from "./hooks";
import { AssetsIcon, ChatIcon, PlayersIcon, TradeIcon } from "./icons";
import { MortgageDecisionDialog } from "./MortgageDecisionDialog";
import { PlayerAssetsSheet } from "./PlayerAssetsSheet";
import { PlayerList } from "./PlayerList";
import { ReactionBubbles } from "./ReactionBubbles";
import { TradePanel } from "./TradePanel";
import { useHotkeys } from "./useHotkeys";
import { useUiStore } from "./uiStore";
import type { GameTab } from "./uiStore";

interface TabDef {
  id: GameTab;
  label: string;
  icon: ReactNode;
  badge: number;
  badgeLabel: string;
}

const TAB_TITLES: Record<GameTab, string> = { players: "Spieler", assets: "Besitz", trade: "Handel", chat: "Chat & Log" };

function Badge({ count, label }: { count: number; label: string }) {
  if (count <= 0) return null;
  return (
    <span className="grid min-w-5 place-items-center rounded-full bg-accent px-1.5 text-xs font-bold leading-5 text-accent-ink">
      {count}
      <span className="sr-only"> {label}</span>
    </span>
  );
}

function Panel({ tab, room, visible }: { tab: GameTab; room: RoomView; visible: boolean }) {
  switch (tab) {
    case "players":
      return <PlayerList room={room} />;
    case "assets":
      return <AssetsPanel room={room} />;
    case "trade":
      return <TradePanel room={room} />;
    case "chat":
      return <ChatLogPanel room={room} visible={visible} />;
  }
}

/** Sidebar of the desktop layout. All panels stay mounted so drafts survive a tab change. */
function Sidebar({ room, tabs }: { room: RoomView; tabs: TabDef[] }) {
  const active = useUiStore((s) => s.activeTab);
  const setActive = useUiStore((s) => s.setActiveTab);
  const current = tabs.some((t) => t.id === active) ? active : "players";
  return (
    <aside className="hidden w-96 flex-none flex-col border-l border-line bg-surface lg:flex xl:w-[26rem]" aria-label="Seitenleiste">
      <div role="tablist" aria-label="Bereiche" className="grid flex-none border-b border-line" style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}>
        {tabs.map((t) => (
          <button
            key={t.id}
            role="tab"
            type="button"
            id={`gb-tab-${t.id}`}
            aria-selected={current === t.id}
            aria-controls={`gb-panel-${t.id}`}
            onClick={() => setActive(t.id)}
            className={`relative flex min-h-12 items-center justify-center whitespace-nowrap border-b-2 px-2 text-sm font-semibold transition-colors ${
              current === t.id ? "border-accent text-ink" : "border-transparent text-ink-2 hover:bg-surface-2 hover:text-ink"
            }`}
          >
            {t.label}
            {t.badge > 0 && (
              <span className="absolute right-0.5 top-0.5">
                <Badge count={t.badge} label={t.badgeLabel} />
              </span>
            )}
          </button>
        ))}
      </div>
      {tabs.map((t) => (
        <div
          key={t.id}
          role="tabpanel"
          id={`gb-panel-${t.id}`}
          aria-labelledby={`gb-tab-${t.id}`}
          hidden={current !== t.id}
          className="min-h-0 flex-1 overflow-y-auto p-4"
        >
          <Panel tab={t.id} room={room} visible={current === t.id} />
        </div>
      ))}
    </aside>
  );
}

/** Icon bar at the bottom of the phone layout. Each tab opens a bottom sheet. */
function MobileTabBar({ tabs }: { tabs: TabDef[] }) {
  const sheet = useUiStore((s) => s.sheet);
  const openTab = useUiStore((s) => s.openTab);
  return (
    <nav
      aria-label="Bereiche"
      className="grid flex-none border-t border-line bg-surface pb-[env(safe-area-inset-bottom)] lg:hidden"
      style={{ gridTemplateColumns: `repeat(${tabs.length}, minmax(0, 1fr))` }}
    >
      {tabs.map((t) => (
        <button
          key={t.id}
          type="button"
          onClick={() => openTab(t.id)}
          aria-haspopup="dialog"
          aria-expanded={sheet === t.id}
          className="relative flex min-h-14 flex-col items-center justify-center gap-0.5 text-xs font-semibold text-ink-2 hover:bg-surface-2 hover:text-ink"
        >
          {t.icon}
          {TAB_TITLES[t.id]}
          {t.badge > 0 && (
            <span className="absolute right-[calc(50%-1.75rem)] top-1.5">
              <Badge count={t.badge} label={t.badgeLabel} />
            </span>
          )}
        </button>
      ))}
    </nav>
  );
}

/** Money and current player under the board on phones. */
function MobileInfo({ room }: { room: RoomView }) {
  const game = room.game!;
  const me = selfPlayerOf(game, room.you.playerId);
  const cur = currentPlayerOf(game);
  const playing = me !== null && !me.eliminated;
  return (
    <div className="grid grid-cols-2 gap-2 px-3 py-2 lg:hidden">
      <div className="rounded-xl bg-surface-2 px-3 py-1.5">
        <p className="text-xs text-ink-2">{playing ? "Dein Geld" : "Spielstand"}</p>
        <p className="text-lg font-bold tabular-nums">{playing ? formatMoney(me.cash) : `Zug ${game.turnCounter + 1}`}</p>
      </div>
      <div className="flex items-center gap-2 rounded-xl bg-surface-2 px-3 py-1.5">
        <TokenIcon token={cur.token} color={cur.color} size={30} title={cur.name} />
        <div className="min-w-0">
          <p className="text-xs text-ink-2">Am Zug</p>
          <p className="truncate font-semibold">{cur.id === room.you.playerId ? "Du" : cur.name}</p>
        </div>
      </div>
    </div>
  );
}

export function GameScreen({ room }: { room: RoomView }) {
  const game = room.game;
  const desktop = useIsDesktop();
  const sheet = useUiStore((s) => s.sheet);
  const closeSheet = useUiStore((s) => s.closeSheet);
  const selectedField = useUiStore((s) => s.selectedField);
  const selectField = useUiStore((s) => s.selectField);
  const chatSeenId = useUiStore((s) => s.chatSeenId);
  const entries = useGameLog(room);
  useEventEffects();
  const spectating = isSpectating(room);
  useHotkeys(room, spectating ? ["players", "chat"] : ["players", "assets", "trade", "chat"]);

  if (!game) return null;
  const selfId = room.you.playerId;
  const canResign = !spectating && game.phase !== "GAME_OVER" && game.players.filter((p) => !p.eliminated).length > 1;

  const tabs: TabDef[] = [
    { id: "players", label: TAB_TITLES.players, icon: <PlayersIcon />, badge: 0, badgeLabel: "" },
    ...(spectating
      ? []
      : ([
          { id: "assets", label: TAB_TITLES.assets, icon: <AssetsIcon />, badge: 0, badgeLabel: "" },
          {
            id: "trade",
            label: TAB_TITLES.trade,
            icon: <TradeIcon />,
            badge: openTradesToMe(game, selfId),
            badgeLabel: "offene Angebote an dich",
          },
        ] satisfies TabDef[])),
    {
      id: "chat",
      label: TAB_TITLES.chat,
      icon: <ChatIcon />,
      badge: unreadChat(room.chat, chatSeenId, selfId),
      badgeLabel: "ungelesene Nachrichten",
    },
  ];

  return (
    <div className="flex h-dvh flex-col overflow-hidden bg-bg text-ink">
      <GameHeader room={room} canResign={canResign} />

      <div className="flex min-h-0 flex-1">
        <main className="flex min-w-0 flex-1 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto lg:flex lg:flex-col lg:overflow-hidden">
            <div className="relative p-1.5 lg:min-h-0 lg:flex-1 lg:p-3 lg:[container-type:size]">
              <div className="mx-auto aspect-square w-full max-w-[44rem] lg:absolute lg:inset-3 lg:mx-0 lg:grid lg:aspect-auto lg:max-w-none lg:place-items-center">
                <div className="size-full lg:[height:min(100cqw,100cqh)] lg:[width:min(100cqw,100cqh)]">
                  <Board
                    game={game}
                    members={room.members}
                    selfId={selfId}
                    selectedField={selectedField}
                    onFieldClick={selectField}
                    center={<CompactLog entries={entries} count={6} />}
                  />
                </div>
              </div>
              <CardOverlay />
            </div>
            <MobileInfo room={room} />
          </div>
          <ActionBar room={room} />
        </main>
        {desktop && <Sidebar room={room} tabs={tabs} />}
      </div>

      <MobileTabBar tabs={tabs} />
      <ReactionBubbles room={room} />

      {!desktop && sheet !== null && (
        <Sheet open onClose={closeSheet} title={TAB_TITLES[sheet]} wide={sheet === "trade" || sheet === "assets"}>
          <div className={sheet === "chat" ? "h-[65dvh]" : ""}>
            <Panel tab={sheet} room={room} visible />
          </div>
        </Sheet>
      )}

      <FieldSheet room={room} />
      <PlayerAssetsSheet room={room} />
      <MortgageDecisionDialog room={room} />
      <AuctionDialog room={room} />
      <HotkeyHelp />
    </div>
  );
}
